import { useEffect, useState, useMemo } from "react";
import { RegulifyService } from "../api/RegulifyService";
import { usePDDSelectedDeviceStore } from "../store/useDeviceCodeSelectionStore";

const DeviceDiscovery = ({ apiUrl }) => {
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const MAX_SELECTION = 2;
  // Search and filter states
  const [productCodeSearch, setProductCodeSearch] = useState("");
  const [localFilter, setLocalFilter] = useState("");

  const selectedDevices = usePDDSelectedDeviceStore(
    (state) => state.selectedDevices
  );
  const toggleDeviceSelection = usePDDSelectedDeviceStore(
    (state) => state.toggleDeviceSelection
  );
  const clearSelectedDevices = usePDDSelectedDeviceStore(
    (state) => state.clearSelectedDevices
  );

  const [sortOrder, setSortOrder] = useState("desc"); // default latest first
  const handleDecisionDateSort = () => {
  setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
};

const getTime = (val) => {
  if (!val || typeof val !== "string") return 0;

  const parts = val.trim().split("/");
  if (parts.length !== 3) return 0;

  const [mm, dd, yyyy] = parts.map(Number);
  return new Date(yyyy, mm - 1, dd).getTime();
};

const sortedDevices = useMemo(() => {
  return filteredDevices
    .map((device, index) => ({
      ...device,
      _uid: `${device.knumber || "NA"}-${index}`, // unique, no applicant/date used
    }))
    .sort((a, b) => {
      const dateA = getTime(a.decisiondate);
      const dateB = getTime(b.decisiondate);

      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
}, [filteredDevices, sortOrder]);

  const handleProductCodeSearch = async (e) => {
    e.preventDefault();

    const trimmedCode = productCodeSearch.trim();
    if (!trimmedCode) {
      setError("Please enter a product code to search.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      setLoading(true, "Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");
      const response =
        await RegulifyService.pdd_list_all_devices_for_prodcode(trimmedCode);
      const data = response;
      setDevices(data);
      setFilteredDevices(data);
      clearSelectedDevices();
    } catch (err) {
      console.error("Error searching by product code:", err);
      setError("No devices found for that product code.");
      setDevices([]);
      setFilteredDevices([]);
    } finally {
      setLoading(false);
    }
  };

  /* Local client-side filter (on already-fetched data) */
  useEffect(() => {
    if (!localFilter.trim()) {
      setFilteredDevices(devices);
    } else {
      const lower = localFilter.toLowerCase();
      setFilteredDevices(
        devices.filter(
          (d) =>
            d.applicant?.toLowerCase().includes(lower) ||
            d.productcode?.toLowerCase().includes(lower) ||
            d.devicename?.toLowerCase().includes(lower) ||
            d.knumber?.toLowerCase().includes(lower) ||
            d.regulationNumber?.toLowerCase().includes(lower) ||
            d.Classification?.toLowerCase().includes(lower),
        ),
      );
    }
  }, [localFilter, devices]);

  /* Handle 510K Number click to open correct FDA page */
  const handleKNumberClick = (knumber) => {
    if (!knumber) return;

    if (knumber.startsWith("DEN")) {
      window.open(
        `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/denovo.cfm?id=${knumber}`,
        "_blank",
      );
    } else if (knumber.startsWith("K")) {
      window.open(
        `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=${knumber}`,
        "_blank",
      );
    } else if (knumber.startsWith("P")) {
      window.open(
        `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpma/pma.cfm?id=${knumber + "S001"}`,
        "_blank",
      );
    }
  };

const isSelected = (device) =>
  selectedDevices.some((d) => d._uid === device._uid);

  const isMaxReached = selectedDevices.length >= 2;

  return (
    <div className="border-base-300 bg-base-100 flex h-full w-full flex-col rounded-lg border p-4 shadow-sm">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Device List</h2>
      </div>

      {/* Search Controls */}
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Backend Product Code Search */}
        <form
          onSubmit={handleProductCodeSearch}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            placeholder="Enter Product Code to search"
            className="input input-bordered input-sm w-full"
            value={productCodeSearch}
            onChange={(e) => setProductCodeSearch(e.target.value)}
          />
          <button
            type="submit"
            className="btn btn-sm btn-primary tooltip tooltip-right"
            data-tip="Enter your device code of interest and click to find all similar devices"
            disabled={loading}
          >
            Search
          </button>
        </form>

        {/* Local Filter */}
        <div className="form-control">
          <input
            type="text"
            placeholder="Filter results by any field"
            className="input input-bordered input-sm w-full"
            value={localFilter}
            onChange={(e) => setLocalFilter(e.target.value)}
            disabled={devices.length === 0}
          />
        </div>
      </div>
      <div className="bg-base-200 flex items-center justify-between px-4 py-2 text-sm">
        <span>
          Selected: {selectedDevices.length} / {MAX_SELECTION}
        </span>
        <span>
          Total Devices Found: {devices.length}
        </span>
        {isMaxReached && (
          <span className="text-warning">
            You can select a maximum of 2 devices for creating a pre-submission draft.
          </span>
        )}
      </div>

      {/* Table Content */}
      <div className="border-base-300 flex-1 overflow-auto rounded-md border">
        {loading ? (
          <div className="text-base-content/60 flex h-40 items-center justify-center">
            <span className="loading loading-spinner loading-md"></span>
          </div>
        ) : error ? (
          <div className="alert alert-error m-4 text-sm">
            <span>{error}</span>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="text-base-content/60 p-4 text-sm">
            {devices.length === 0
              ? "Enter a Product Code and click Search to view devices."
              : "No devices match the current filter."}
          </div>
        ) : (
          <table className="table-sm table w-full">
            <thead>
              <tr className="bg-base-200">
                <th className="w-10"></th>
                <th>Applicant</th>
               <th
                onClick={handleDecisionDateSort}
                className="cursor-pointer select-none"
              >
                Decision Date {sortOrder === "asc" ? "↑" : "↓"}
              </th>
                <th>Product Code</th>
                <th>Device Name</th>
                <th>510K Number</th>
                <th>regulationNumber</th>
                <th>Classification</th>
              </tr>
            </thead>
            <tbody>
             {sortedDevices.map((device) => {
                const selected = isSelected(device.knumber);
                const disableCheckbox = !selected && isMaxReached;

                return (
                  <tr
                    key={device._uid}
                    className={`hover:bg-base-200 transition-colors ${selected ? "bg-primary/10" : ""
                      }`}
                  >
                    {/* NEW CHECKBOX CELL */}
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm checkbox-primary"
                        checked={isSelected(device)}
                        disabled={!isSelected(device) && selectedDevices.length >= 2}
                        onChange={() => toggleDeviceSelection(device)}
                      />
                    </td>

                    <td className="max-w-[200px] truncate">{device.applicant}</td>
                    <td>{device.decisiondate}</td>
                    <td>{device.productcode}</td>
                    <td className="max-w-[200px] truncate">
                      {device.devicename}
                    </td>
                    <td>
                      {device.knumber ? (
                        <button
                          onClick={() => handleKNumberClick(device.knumber)}
                          className="link link-primary text-sm"
                        >
                          {device.knumber}
                        </button>
                      ) : (
                        <span className="text-base-content/60">N/A</span>
                      )}
                    </td>
                    <td>{device.regulationNumber}</td>
                    <td>{device.Classification}</td>
                  </tr>
                );
              })}

            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DeviceDiscovery;
